/**
 * 单飞刷新 —— 契约 §8.4。
 *
 * access 令牌 **5 分钟**过期(后端故意不做黑名单)。并发的多个请求同时撞到过期是常态，
 * 每人各刷一次会把 refresh 的滑动续期打乱，也会白白撞限流(A5 档位 200)。
 *
 * ⚠ 这里最容易写错的不是「只刷一次」，而是下面两处：
 *   1. 请求发出后、401 回来前，**别人可能已经刷完了** —— 此时不该再刷一次，
 *      直接拿新令牌重放即可(见 `handleUnauthorized` 的第一段)；
 *   2. 刷新失败时，排队的请求必须**拿到同一个终态**、登录框**只弹一次** ——
 *      靠的是 `onAuthLost` 在单飞函数**内部**触发。
 */
import type { AuthResponse } from '@cione/contracts'
import { withoutCrossTabLock, type WithLock } from './cross-tab-lock'
import type { ApiError } from './errors'
import { singleFlight } from './single-flight'
import { toExpiresAt, type TokenStore } from './token-store'

/**
 * 跨标签页刷新锁的名字。⚠ 同源内所有标签必须用同一个字符串，改名等于关掉这道保护。
 */
const REFRESH_LOCK = 'cione.auth.refresh'

export type AuthLostReason = 'no-refresh-token' | 'refresh-failed'

export interface RefreshRunnerOptions {
  tokenStore: TokenStore
  /**
   * 真正调 A5 的那一下。抽成参数是为了不让本模块依赖 auth.ts ——
   * 否则 auth 要 http、http 要 refresh、refresh 要 auth，绕成一个环。
   */
  callRefresh: (refreshToken: string) => Promise<AuthResponse>
  /** ⚠ 令牌彻底失效。整个刷新周期内**只会触发一次**，所以登录框也只弹一次。 */
  onAuthLost?: ((reason: AuthLostReason) => void) | undefined
  now?: (() => number) | undefined
  /**
   * 跨标签页互斥(W1-3)。默认不协调 —— `createApiClient` 会在浏览器里接上 Web Locks。
   * ⚠ 它只解决**同一个浏览器内的多标签**；「手机 App 与电脑网页同时在线」那半
   *   是服务端要不要给 refresh 加宽限期的问题，前端修不了，别以为接上就万事大吉。
   */
  withLock?: WithLock | undefined
}

export interface RefreshRunner {
  /** 撞到 401 时调用。true = 已换到新令牌，可以原样重放一次。 */
  handleUnauthorized(error: ApiError, accessTokenUsed: string | null): Promise<boolean>
  /** 主动刷一次(M1 的「提前续期」会用到)。并发调用只会真刷一次。 */
  refresh(): Promise<boolean>
}

export function createRefreshRunner(options: RefreshRunnerOptions): RefreshRunner {
  const now = options.now ?? Date.now

  /**
   * 令牌已经彻底失效，在重新登录前不要再刷。
   *
   * ⚠ 这一位不是优化，是修一个并发下的真 bug：单飞只保证「同时在飞的合成一个」。
   *   刷新失败并清空令牌之后，**晚到的那几个 401** 会各自再起一次刷新 ——
   *   于是 `onAuthLost` 触发多次，用户看到登录框弹好几遍。
   */
  let authLost = false
  // 重新拿到令牌(登录成功 / 领了匿名)就解除封印
  options.tokenStore.subscribe((tokens) => {
    if (tokens) authLost = false
  })

  const withLock = options.withLock ?? withoutCrossTabLock

  const refresh = singleFlight(async (): Promise<boolean> => {
    if (authLost) return false

    const seen = options.tokenStore.get()?.refreshToken
    if (!seen) {
      authLost = true
      options.tokenStore.set(null)
      options.onAuthLost?.('no-refresh-token')
      return false
    }

    // ⚠ 跨标签页互斥见 cross-tab-lock.ts：后端 refresh 一次性，两个标签各刷各的必然踢人
    return withLock(REFRESH_LOCK, async () => {
      /*
       * ⚠⚠ 拿到锁之后**必须重读**令牌 —— 等锁的这段时间里，别的标签页可能已经刷完了。
       *   照着进锁前那张 `seen` 去刷 = 拿一张**已经被消费掉**的 refresh 令牌，
       *   必然失败，然后我们会把人家刚换来的好令牌清掉，把用户踢去登录。
       *   这一步漏了的话，加锁反而比不加锁更糟(排队排出一串必败的刷新)。
       */
      const current = options.tokenStore.get()?.refreshToken

      if (authLost) return false

      if (!current) {
        // 别的标签刷失败并清空了令牌：跟着走同一个终态，不要再刷一次
        authLost = true
        options.onAuthLost?.('refresh-failed')
        return false
      }

      // 令牌换过了 = 别人刚刷成功。直接用新的重放，不再消费一次
      if (current !== seen) return true

      try {
        const auth = await options.callRefresh(current)
        options.tokenStore.set({
          accessToken: auth.tokens.accessToken,
          refreshToken: auth.tokens.refreshToken,
          expiresAt: toExpiresAt(auth.tokens.expiresIn, now()),
        })
        return true
      } catch {
        // refresh 也失效了：清干净再通知，别让界面拿着废令牌继续试
        authLost = true
        options.tokenStore.set(null)
        options.onAuthLost?.('refresh-failed')
        return false
      }
    })
  })

  return {
    refresh,
    handleUnauthorized(_error, accessTokenUsed) {
      const current = options.tokenStore.get()?.accessToken ?? null
      // ⚠ 令牌已经不是我们发请求时用的那个 = 别人刚刷过。直接重放，不要再刷一次。
      if (current && accessTokenUsed && current !== accessTokenUsed) {
        return Promise.resolve(true)
      }
      return refresh()
    },
  }
}
